import type { StagePlaintext, StageKdfParams } from '@ers/shared';
import {
  AES_GCM_IV_LENGTH,
  canonicalizeToBytes,
  concatBytes,
  decryptAesGcm,
  deriveHkdfBytes,
  fromBase64Url,
  importAesKey,
  RECOVERY_DOMAIN_PREFIX,
  utf8Encode,
  toBase64Url,
  sha256,
  CHAIN_DOMAIN_UPDATE,
  CHAIN_DOMAIN_FILE,
} from '@ers/shared';
import { deriveArgon2id } from '@ers/shared/crypto/argon2/browser';
import { VoprfSession } from '@ers/shared/crypto/voprf';
import { z } from 'zod';

import { getDefaultPinnedKeyBase64 } from '../config/runtime.js';
import { fetchStagePayload } from '../lib/fetch.js';

import { stageFetchResponseSchema } from './schema.js';
import type { StagePayloadParsed } from './schema.js';

const plaintextSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('intermediate'),
    next_hints: z.array(z.string()).optional(),
    next_salt: z.string(),
    next_iv: z.string(),
    next_kdf_params: z.object({
      hint: z.string(),
      minMemoryMiB: z.number().int().min(16),
      memoryMiB: z.number().int().min(16).optional(),
      timeCost: z.number().int().min(1).optional(),
      parallelism: z.number().int().min(1).optional(),
    }),
    next_nonce: z.string(),
  }),
  z.object({
    type: z.literal('final'),
    recovery_data: z.unknown(),
  }),
]);

export interface StageProcessOptions {
  answer: string;
  turnstileToken?: string;
  workerBaseUrl: string;
  currentStageUrl?: string;
  chainKeyPrevB64?: string;
  currentParams?: {
    saltB64: string;
    ivB64: string;
    kdfParams: StageKdfParams;
  };
}

export interface StageProcessResult {
  plaintext: StagePlaintext;
  argonProfileMemoryMiB: number;
  durationMs: number;
  nextStageUrl?: string;
  chainKeyNextB64: string;
  turnstileRequired: boolean;
  nextState?: {
    saltB64: string;
    ivB64: string;
    kdfParams: {
      hint: string;
      minMemoryMiB: number;
      memoryMiB?: number;
      timeCost?: number;
      parallelism?: number;
    };
  };
}

export class StageDecryptionError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'StageDecryptionError';
  }
}

function resolvePinnedKey(): { bytes: Uint8Array; base64: string } {
  const base64 = getDefaultPinnedKeyBase64();
  if (!base64) {
    throw new Error('Pinned VOPRF key not set');
  }
  return { bytes: fromBase64Url(base64), base64 };
}

async function callVoprf(
  body: {
    blinded_b64: string;
    turnstile_token?: string;
  },
  workerBaseUrl: string,
): Promise<{
  evaluated_b64: string;
  proof_b64: string;
  server_pubkey_b64: string;
  turnstile_required: boolean;
}> {
  const response = await fetch(`${workerBaseUrl.replace(/\/$/, '')}/oprf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Worker error ${response.status}: ${text}`);
  }

  const apiResponse = (await response.json()) as {
    success: boolean;
    data?: {
      evaluated_b64: string;
      proof_b64: string;
      server_pubkey_b64: string;
      turnstile_required: boolean;
    };
    error?: {
      code: string;
      message: string;
      retryAfterSeconds?: number;
    };
  };

  if (!apiResponse.success || !apiResponse.data) {
    const error = apiResponse.error;
    if (error) {
      if (error.code === 'RATE_LIMITED' && error.retryAfterSeconds) {
        const message = `Rate limited. Retry after ${error.retryAfterSeconds} seconds.`;
        throw Object.assign(new Error(message), {
          code: 429,
          retryAfter: error.retryAfterSeconds,
        });
      }
      if (error.code === 'TURNSTILE_FAILED') {
        throw Object.assign(
          new Error(`Turnstile verification failed: ${error.message}`),
          { code: 'TURNSTILE_FAILED' as const },
        );
      }
      throw Object.assign(new Error(`[${error.code}] ${error.message}`), {
        code: error.code,
      });
    }
    throw new Error('Invalid response from worker');
  }

  return apiResponse.data;
}

function buildInputBytes(answerBytes: Uint8Array): Uint8Array {
  const domain = utf8Encode(RECOVERY_DOMAIN_PREFIX);
  const zero = new Uint8Array([0]);
  return concatBytes(domain, zero, answerBytes);
}

function buildAadBytesForInitial(
  usedSaltB64: string,
  usedIvB64: string,
  usedKdfParams: {
    hint: string;
    minMemoryMiB: number;
    memoryMiB?: number;
    timeCost?: number;
    parallelism?: number;
  },
): Uint8Array {
  return canonicalizeToBytes({
    salt: usedSaltB64,
    iv: usedIvB64,
    kdf_params: usedKdfParams,
  });
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a[i] ^ b[i];
  }
  return diff === 0;
}

export async function loadStage(url: string): Promise<StagePayloadParsed> {
  const response = await fetchStagePayload(url);
  if (!response.ok) {
    throw new Error(`Failed to load stage ${url}: ${response.status}`);
  }
  const json = await response.json();
  const parsed = stageFetchResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error(`Stage payload validation error: ${parsed.error.message}`);
  }
  return parsed.data.data;
}

export async function processStage(
  payload: StagePayloadParsed,
  options: StageProcessOptions,
): Promise<StageProcessResult> {
  const answerBytes = utf8Encode(options.answer);

  const { bytes: pinnedKeyBytes, base64: pinnedBase64 } = resolvePinnedKey();
  const inputBytes = buildInputBytes(answerBytes);
  const session = await VoprfSession.create(pinnedKeyBytes, inputBytes);

  const blindedB64 = session.getRequestBase64();

  const result = await callVoprf(
    {
      blinded_b64: blindedB64,
      turnstile_token: options.turnstileToken,
    },
    options.workerBaseUrl,
  );

  if (result.server_pubkey_b64 !== pinnedBase64) {
    const serverKeyBytes = fromBase64Url(result.server_pubkey_b64);
    if (!equalBytes(pinnedKeyBytes, serverKeyBytes)) {
      throw new Error('Server public key mismatch');
    }
  }

  session.verifyProof(result.evaluated_b64, result.proof_b64);
  const oprfOutput = await session.finalize(result.evaluated_b64);

  if (!options.currentParams) {
    throw new StageDecryptionError('Missing stage parameters');
  }
  const usedSaltB64 = options.currentParams.saltB64;
  const usedIvB64 = options.currentParams.ivB64;
  const usedKdfParams = options.currentParams.kdfParams;

  const saltBytes = fromBase64Url(usedSaltB64 as string);
  const overrideProfile = usedKdfParams.memoryMiB
    ? {
        memoryMiB: usedKdfParams.memoryMiB,
        timeCost: usedKdfParams.timeCost ?? 1,
        parallelism: usedKdfParams.parallelism ?? 2,
      }
    : undefined;

  const {
    hash: argonHash,
    profile,
    durationMs,
  } = await deriveArgon2id(
    answerBytes,
    saltBytes,
    usedKdfParams.minMemoryMiB,
    overrideProfile,
  );

  const chainPrev: Uint8Array = options.chainKeyPrevB64
    ? fromBase64Url(options.chainKeyPrevB64)
    : new Uint8Array(0);
  const isInitial = chainPrev.length === 0;

  const ikm = isInitial
    ? concatBytes(argonHash, oprfOutput)
    : concatBytes(argonHash, oprfOutput, chainPrev);
  const hkdfBytes = await deriveHkdfBytes(ikm, saltBytes);

  const aesKey = await importAesKey(hkdfBytes, ['decrypt']);
  const ivBytes = fromBase64Url(usedIvB64 as string);
  if (ivBytes.length !== AES_GCM_IV_LENGTH) {
    throw new Error('Invalid IV length');
  }

  const ciphertext = fromBase64Url(payload.ciphertext);
  const aad = isInitial
    ? buildAadBytesForInitial(usedSaltB64 as string, usedIvB64 as string, usedKdfParams)
    : canonicalizeToBytes({});

  let plaintextBytes: Uint8Array;
  try {
    plaintextBytes = await decryptAesGcm(aesKey, ivBytes, ciphertext, aad);
  } catch (error) {
    throw new StageDecryptionError('Incorrect answer or stage data is corrupted.', {
      cause: error,
    });
  }
  const plaintext = JSON.parse(new TextDecoder().decode(plaintextBytes));
  const validated = plaintextSchema.parse(plaintext);

  const chainLabel = utf8Encode(`${CHAIN_DOMAIN_UPDATE}`);
  const chainKeyNext = await sha256(concatBytes(chainLabel, ikm));
  let nextStageUrl: string | undefined;
  if (validated.type === 'intermediate') {
    const nonceBytes = fromBase64Url(validated.next_nonce);
    const fileLabel = utf8Encode(`${CHAIN_DOMAIN_FILE}`);
    const nextToken = await sha256(concatBytes(fileLabel, chainKeyNext, nonceBytes));
    const nextTokenB64 = toBase64Url(nextToken);
    if (options.currentStageUrl) {
      try {
        // Try to parse as absolute URL and update the token
        const baseUrl = new URL(options.currentStageUrl);
        baseUrl.search = `token=${encodeURIComponent(nextTokenB64)}`;
        nextStageUrl = baseUrl.toString();
      } catch {
        // If it's a relative URL, construct it manually
        const basePath = options.currentStageUrl.split('?')[0];
        nextStageUrl = `${basePath}?token=${encodeURIComponent(nextTokenB64)}`;
      }
    } else {
      nextStageUrl = `${options.workerBaseUrl}/stage?token=${encodeURIComponent(nextTokenB64)}`;
    }
  }

  return {
    plaintext: validated as StagePlaintext,
    argonProfileMemoryMiB: profile.memoryMiB,
    durationMs,
    nextStageUrl,
    chainKeyNextB64: toBase64Url(chainKeyNext),
    turnstileRequired: result.turnstile_required,
    nextState:
      validated.type === 'intermediate'
        ? {
            saltB64: validated.next_salt,
            ivB64: validated.next_iv,
            kdfParams: validated.next_kdf_params,
          }
        : undefined,
  };
}
